/* eslint-disable @typescript-eslint/no-use-before-define */
import moment from 'moment';
import { pluck } from 'ramda';
import * as React from 'react';
import {
  AsyncSelect,
  Button,
  Input,
  Modal,
  ReactSelect,
  Select,
  ToggleInput,
} from '../..';
import { showAlert, useAuth } from '../../../context/authContext';
import { handleInventoryAction } from '../../../context/miscContext';
import { fetchSearchItems, getSaleItem } from '../../../context/storeContext';
import useFetchAsync from '../../../hooks/useFetchAsync';
import useValidator from '../../../hooks/useValidator';
import { Product } from '../../../pages/Inventory/Inventory';
import { SaleItem } from '../../../pages/POSTeller/POSTeller';
import { Supplier } from '../../../pages/Suppliers/Suppliers';
import { Option } from '../../Inputs/SelectInput/SelectInput';

type ModalProps = {
  onCloseModal: () => void;
  onSetNewItem: (saleItem: SaleItem) => void;
  storeId: number;
};

function ManualSale({ onCloseModal, onSetNewItem, storeId }: ModalProps) {
  const [{ storePreferences }, dispatch] = useAuth();
  const [product, setProduct] = React.useState({
    name: '',
    categoryId: '',
    brandId: '',
    supplierId: '',
    price: '',
    quantity: 1,
    upc: '',
    taxes: '',
    status: 'In Stock',
    inStockDate: moment().format('YYYY-MM-DD'),
    stockThreshold: 0,
    trackInventory: true,
    trackWholesale: false,
  });
  const [errors, setErrors] = React.useState({
    name: false,
    price: false,
    quantity: false,
  });
  const { data, status, error, run } = useFetchAsync();
  const [validator, showValidationMessage] = useValidator({});
  const [chargeTax, setChargeTax] = React.useState(false);

  const getSaleItemCall = React.useCallback(getSaleItem, [storePreferences]);

  React.useEffect(() => {
    if (status === 'REJECTED' && error?.message) {
      showAlert(
        dispatch,
        {
          show: true,
          message:
            error?.message ||
            'Oops. Something unusual happened at our end. Please contact our support team!',
          type: 'ERROR',
        },
        { autoDismiss: true, time: 3200 },
      );
    }

    if (status === 'RESOLVED' && data) {
      const { product: getProduct } = data as any;
      const saleItem = {
        ...getSaleItemCall(
          getProduct as Product,
          storePreferences?.taxes || [],
        ),
        upc:
          getSaleItemCall(getProduct as Product, storePreferences?.taxes || [])
            .upc || '',
      };
      onSetNewItem(saleItem);
      onCloseModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    dispatch,
    error?.message,
    getSaleItemCall,
    status,
    storePreferences?.taxes,
  ]); // No need to call onSetNewItem() and onCloseModal()

  const handleProductInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = event.target;

    setProduct(fndProduct => ({
      ...fndProduct,
      [name]: type !== 'number' ? value : (event.target as any)?.valueAsNumber,
    }));
  };

  const handleSupplierSearch = async (search: string): Promise<Option[]> => {
    if (!storeId) {
      return [];
    }

    const {
      suppliers: { data: getSuppliers },
    } = (await fetchSearchItems(`/suppliers?store=${storeId}`, {
      q: search,
    })) as any;

    const query: Supplier[] = getSuppliers;

    const suppliers = query?.map(fndSupplier => ({
      label: fndSupplier.businessName,
      value: fndSupplier.supplierId,
    }));

    return suppliers;
  };

  const handleItemSave = async () => {
    if (!validator.allValid()) {
      setErrors(errs => ({
        ...errs,
        name: !validator.fieldValid('name'),
        price: !validator.fieldValid('price'),
        quantity: !validator.fieldValid('quantity'),
      }));
      showValidationMessage(true);
      return;
    }

    let selectedTaxes = '';

    if (chargeTax && storePreferences) {
      const withParentIDs = storePreferences.taxes.filter(
        fndTax => fndTax.id.toString() !== product.taxes,
        //  && fndTax.parentId?.toString() === product.taxes,
      );
      selectedTaxes =
        withParentIDs.length > 0
          ? pluck('id', withParentIDs).join('|')
          : product.taxes;
    }

    const fndProduct = {
      ...product,
      taxes: selectedTaxes,
      chargeTax,
    } as any;

    handleInventoryAction(
      run,
      storeId,
      fndProduct,
      showFormActionAlert,
      'post',
    );
  };

  const showFormActionAlert = (message: string) => {
    showAlert(
      dispatch,
      {
        show: true,
        message,
        type: 'ERROR',
      },
      { autoDismiss: true, time: 3200 },
    );
  };

  return (
    <Modal onClose={onCloseModal} show onHide={() => false}>
      <div className="rounded-md bg-white tablet:w-[500px]">
        <form>
          <div className="flex flex-col p-5">
            <h3
              className="text-pos-black text-[20px] font-semibold mb-2
              inline-flex whitespace-pre"
            >
              New Inventory Item - Manual Sale
            </h3>
            <div className="text-pos-black text-sm mt-2 mb-2 font-normal space-y-4">
              <div>
                <label htmlFor="name" className="text-xbase block mb-1">
                  Product name{' '}
                  <span className="text-xs font-light text-pos-red">*</span>
                </label>
                <Input
                  id="name"
                  name="name"
                  value={product.name}
                  onChange={handleProductInputChange}
                  onBlur={() => {
                    setErrors(errs => ({
                      ...errs,
                      name: !validator.check(product.name, 'required|min:3'),
                    }));
                  }}
                  error={errors.name}
                />
                {validator.message('name', product.name, 'required|min:3', {
                  messages: {
                    required: 'Product name is required.',
                  },
                  className:
                    'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                })}
              </div>

              <div className="flex flex-row gap-5">
                <div className="w-[150px] shrink-0">
                  <label htmlFor="price" className="text-xbase block mb-1">
                    Price{' '}
                    <span className="text-xs font-light text-pos-red">*</span>
                  </label>
                  <Input
                    name="price"
                    id="price"
                    value={product.price}
                    min={0}
                    type="number"
                    onChange={handleProductInputChange}
                    onBlur={() => {
                      setErrors(errs => ({
                        ...errs,
                        price: !validator.check(
                          product.price,
                          'required|numeric|min:0,num',
                        ),
                      }));
                    }}
                    error={errors.price}
                  />
                  {validator.message(
                    'price',
                    product.price,
                    'required|numeric|min:0,num',
                    {
                      messages: {
                        required: 'Price is required.',
                      },
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    },
                  )}
                </div>
                <div className="w-[100px] shrink-0">
                  <label htmlFor="quantity" className="text-xbase block mb-1">
                    Qty
                  </label>
                  <Input
                    id="quantity"
                    name="quantity"
                    type="number"
                    min={0}
                    value={product.quantity}
                    onChange={handleProductInputChange}
                  />
                  {validator.message(
                    'quantity',
                    product.quantity,
                    'numeric|min:0,num',
                    {
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    },
                  )}
                </div>
                <div className="flex-1">
                  <label
                    htmlFor="upc"
                    className="text-xbase block mb-1 whitespace-nowrap"
                  >
                    UPC (Barcode)
                  </label>
                  <Input
                    name="upc"
                    id="upc"
                    value={product.upc}
                    onChange={handleProductInputChange}
                  />
                </div>
              </div>

              <div className="flex flex-row space-x-10">
                <div className="">
                  <label htmlFor="chargeTax" className="text-xbase block mb-1">
                    Charge Tax
                  </label>
                  <ToggleInput
                    id="chargeTax"
                    name="chargeTax"
                    value="chargeTax"
                    checked={chargeTax}
                    // onChange={event => setChargeTax(event.target.checked)}
                    onChange={() => false}
                    className="align-middle cursor-pointer"
                  />
                </div>
                <div>
                  <label htmlFor="taxes" className="text-xbase block mb-1">
                    Tax Rate
                  </label>
                  <Select
                    placeholder={{
                      value: '',
                      label: `${!chargeTax ? 'No Tax' : 'Select tax rate'}`,
                    }}
                    disabled={!chargeTax}
                    value={product.taxes}
                    options={
                      storePreferences && storePreferences?.taxes.length > 0
                        ? storePreferences.taxes.map(fndTax => ({
                            value: fndTax.id,
                            label: fndTax.name,
                          }))
                        : []
                    }
                    name="taxes"
                    id="taxes"
                    onChange={handleProductInputChange}
                    className="w-[200px]"
                  />
                </div>
              </div>

              <div className="!mt-5">
                <div className="border-b border-[#d0d7de]">
                  <h2 className="text-xbase font-semibold">Others</h2>
                </div>
                <div className="mt-2 mb-3">
                  <label htmlFor="supplierId" className="text-xbase block mb-1">
                    Supplier
                  </label>
                  <AsyncSelect
                    name="supplierId"
                    id="supplierId"
                    placeholder="Select supplier"
                    noOptionsMessage={() => 'No supplier found!'}
                    cacheOptions
                    isClearable
                    loadOptions={handleSupplierSearch}
                    value={product.supplierId}
                    onChange={option =>
                      setProduct(fndProduct => ({
                        ...fndProduct,
                        supplierId: option,
                      }))
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (provided: any) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                      menu: (provided: any) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                    }}
                  />
                </div>
                <div className="flex flex-row gap-5">
                  <div className="w-full">
                    <label
                      htmlFor="categoryId"
                      className="text-xbase block mb-1"
                    >
                      Category
                    </label>
                    <ReactSelect
                      name="categoryId"
                      id="categoryId"
                      placeholder="Select category"
                      noOptionsMessage={() => 'No category found!'}
                      value={product.categoryId}
                      isClearable
                      onChange={option =>
                        setProduct(fndProduct => ({
                          ...fndProduct,
                          categoryId: option,
                        }))
                      }
                      options={
                        storePreferences &&
                        storePreferences?.categories.length > 0
                          ? storePreferences.categories.map(fndCategory => ({
                              value: fndCategory.id,
                              label: fndCategory.name,
                            }))
                          : []
                      }
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (provided: any) => ({
                          ...provided,
                          zIndex: 9999,
                        }),
                        menu: (provided: any) => ({
                          ...provided,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>
                  <div className="w-full">
                    <label htmlFor="brandId" className="text-xbase block mb-1">
                      Brand
                    </label>
                    <ReactSelect
                      name="brandId"
                      id="brandId"
                      placeholder="Select brand"
                      noOptionsMessage={() => 'No brand found!'}
                      value={product.brandId}
                      isClearable
                      onChange={option =>
                        setProduct(fndProduct => ({
                          ...fndProduct,
                          brandId: option,
                        }))
                      }
                      options={
                        storePreferences && storePreferences?.brands.length > 0
                          ? storePreferences.brands.map(fndBrand => ({
                              value: fndBrand.id,
                              label: fndBrand.name,
                            }))
                          : []
                      }
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (provided: any) => ({
                          ...provided,
                          zIndex: 9999,
                        }),
                        menu: (provided: any) => ({
                          ...provided,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 text-center flex flex-row ml-auto space-x-4">
              <Button
                label="Add"
                onClick={handleItemSave}
                type="button"
                disabled={status === 'LOADING'}
                loading={status === 'LOADING'}
                className="!bg-pos-green border border-[rgba(27,31,36,0.15)] min-w-[150px]  h-[40px]
                      items-center justify-center text-white text-xbase
                      shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium"
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default ManualSale;
